import { getBreakdownTitle } from 'checkout/utils'
import { DMY_CASUAL_FORMAT, INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { arrayToMap, groupBy, sum } from 'lib/array/arrayUtils'
import { countTravellersAsOccupants, formatOccupantsShort, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { formatRoomType, formatPackageOptionType } from 'lib/tours/tourUtils'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import moment from 'moment'
import { createSelector } from 'reselect'
import {
  isTourItem,
  isTourV2ExperienceItem,
  isTourV2Item,
  isTourV2ItemWithPackageUpgrade,
} from 'lib/checkout/checkoutUtils'
import { tourV2ExperienceWithExistingOrder } from 'api/mappers/TourV2/tourV2OfferMapper'
import {
  TOUR_OPTIONAL_EXPERIENCE_ITEM_STATUS_COMPLETED,
} from 'constants/tours'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import { isPostPurchaseAncillaryPayment } from 'checkout/selectors/payment/isPostPurchaseAncillaryPayment'
import uuidV4 from 'lib/string/uuidV4Utils'
import { CHECKOUT_ITEM_TYPE_TOUR_V2 } from 'constants/checkout'
import { capitalise } from 'lib/string/stringUtils'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import getTourV2ExperienceItemsViews from './itemViews/getTourV2ExperienceItemsViews'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

const TOUR_POST_PURCHASES: Array<App.CheckoutCartMode> = ['tour-change-dates', 'tour-optional-experiences']

export const getTourV2ItemsForPackageUpgrade = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.TourV2Item> => items.filter(isTourV2ItemWithPackageUpgrade),
  reselectShallowEqual,
)

export const getTourV2Items = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getOrderTourItems(state),
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (items, orderTourItems, isAncillaryPayment): Array<App.Checkout.TourV2Item> => {
    return isAncillaryPayment ? orderTourItems : items.filter(isTourV2Item)
  },
  reselectShallowEqual,
)

const getOrderTourItems = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.order.tourV2SnapshotDetails,
  (existingOrder, tourV2SnapshotDetails): Array<App.Checkout.TourV2Item> => {
    if (!existingOrder?.tourItems) {
      return []
    }

    return existingOrder?.tourItems.flatMap<App.Checkout.TourV2Item>(item => {
      const travellersByRooms = Array.from(groupBy(
        item.tour.travellers,
        (traveller) => traveller.roomNumber,
      ).values())
      return travellersByRooms.map((travellers: Array<App.Tours.TourV2OrderItemTravellerDetails>) => {
        return {
          itemId: uuidV4(),
          transactionKey: uuidV4(),
          startDate: item.tour.startDate,
          endDate: item.tour.endDate,
          occupancy: countTravellersAsOccupants(travellers),
          duration: item.tour.duration,
          image: { id: tourV2SnapshotDetails?.[item.reservationId]?.imageId },
          isOriginalItem: true,
          originalTotal: item.total,
          itemType: CHECKOUT_ITEM_TYPE_TOUR_V2,
          offerId: item.tourId,
          purchasableOption: {
            fkDepartureId: item.departureId,
            fkSeasonId: item.tour.seasonId,
            fkTourId: item.tourId,
            roomType: travellers[0].roomType as App.Tours.V2OfferRoomType,
            fkVariationId: item.tourOptionId ?? '',
            inventoryLeft: 0,
            fullPrice: 0,
            price: 0,
            memberPrice: 0,
            packageType: item.packageOption?.packageOption,
            packageOptionDisplayName: item.packageOption?.packageOptionDisplayName,
          },
        }
      })
    })
  },
)

export const getTourV2ExperienceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.TourV2ExperienceItem> => items.filter(isTourV2ExperienceItem),
  reselectShallowEqual,
)

export const getTourItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.TourV2Item | App.Checkout.LETourV1Item> => items.filter(isTourItem),
  reselectShallowEqual,
)

export const getExistingTourExperienceItems = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.offer.tourV2Offers,
  (existingOrder, tourOffers): Map<String, App.Checkout.TourV2ExperienceItem> => {
    const tourOffer = existingOrder?.tourItems[0]?.tourId ? tourOffers[existingOrder?.tourItems[0]?.tourId] : undefined
    if (!!existingOrder?.tourExperienceItems?.length && tourOffer?.purchasableExperienceOptions) {
      return arrayToMap(
        existingOrder?.tourExperienceItems?.filter(tourExperienceItem => tourExperienceItem.status === TOUR_OPTIONAL_EXPERIENCE_ITEM_STATUS_COMPLETED),
        (item: App.Tours.TourV2OrderExperienceItem) => `${item.id}_${item.dayNumber}`,
        (item: App.Tours.TourV2OrderExperienceItem) => {
          return tourV2ExperienceWithExistingOrder(item, tourOffer?.purchasableExperienceOptions[item.id])
        },
      )
    }
    return new Map()
  },
)

export function getTourV2BreakdownView(
  offerView: App.Checkout.TourV2AccommodationOfferView,
  hidePrice: boolean,
  postPurchase?: App.CheckoutCartMode,
  commissionData?: App.AgentHubCommission,
  dateFormattingLocale = 'en-AU',
): App.Checkout.PriceBreakdownView {
  const intl = getCurrentIntl()
  const { durationLabel, startDate, endDate, saleUnit, itemViews, offer, mainLabel } = offerView
  const isTourPostPurchase = postPurchase && TOUR_POST_PURCHASES.includes(postPurchase)
  const isOriginalOrderItem = itemViews.some(itemView => itemView.item.isOriginalItem)

  const guestCount = sumUpOccupancies(itemViews.map(itemView => itemView.occupancy))

  const items = itemViews.map<App.Checkout.TourV2ItemBreakdownView>((item: App.Checkout.TourV2AccommodationItemView, index) => {
    const purchasableOption = item.item.purchasableOption
    const packageOptionDisplayName = purchasableOption.packageOptionDisplayName
    const { totalPrice, totalMemberPrice } = generateDepartureRoomPriceBreakdown(
      item.occupancy,
      purchasableOption,
      offer,
    )
    const adultLabel = isSalesforceTourV2(offer) ? 'traveller' : 'adult'

    const packageType = purchasableOption?.roomType ? packageOptionDisplayName || formatPackageOptionType(item.item.offerId)(purchasableOption.roomType) : undefined
    return {
      title: intl.formatMessage(
        {
          id: 'FcrMhSecXSjUFjuDj7lu',
          defaultMessage: '{saleUnit} {number}',
          description: 'Room/cabin number label in tour price breakdown in the checkout tour selection',
        },
        { saleUnit: capitalise(saleUnit), number: index + 1 },
      ),
      additionalInfo: [
        {
          text: purchasableOption.roomType ? intl.formatMessage(
            {
              id: 'z7mLSDeF_EFr3yYFTo2-',
              defaultMessage: '{occupancyLabel} · {roomTypeLabel}',
              description: 'Tour room breakdown occupancy and room type label in the checkout tour selection',
            },
            {
              occupancyLabel: formatOccupantsShort(item.occupancy, undefined, adultLabel),
              roomTypeLabel: formatRoomType(purchasableOption.roomType),
            },
          ) : formatOccupantsShort(item.occupancy, undefined, adultLabel),
        },
      ],
      additionalElements: [],
      price: hidePrice ? undefined : totalPrice,
      memberPrice: hidePrice ? undefined : totalMemberPrice,
      offerId: offer?.id,
      itemId: item.item.itemId,
      itemType: 'tourV2',
      taxesAndFees: item.totals.taxesAndFees,
      packageOption: packageType ?? undefined,
      transactionKey: item.item.transactionKey,
    }
  })
  const purchasableOption = itemViews?.[0]?.item?.purchasableOption
  const selectedVariation = offer?.variations[purchasableOption?.fkVariationId] ??
    Object.values(offer?.variations ?? {}).find(variation => variation.fkTourOptionId === purchasableOption?.fkVariationId)
  const totalCommissionDiscount = !commissionData ? 0 : sum(items, item => commissionData.itemsCommission[getAgentHubCommissionItemKey(item)]?.itemDiscount ?? 0)
  const commissionPercentage = Number((totalCommissionDiscount / sum(items, item => item.price || 0) * 100).toFixed(2))
  return {
    title: getBreakdownTitle(offerView, intl),
    offerType: offer?.type,
    description: mainLabel,
    additionalInfoText: [
        `${intlDateFormatter(startDate, INTL_DMY_CASUAL_FORMAT, dateFormattingLocale)} - ${intlDateFormatter(endDate, INTL_DMY_CASUAL_FORMAT, dateFormattingLocale)}`,
        intl.formatMessage(
          {
            id: 'u0qbyWjA3saC0vLXFUeu',
            defaultMessage: '{durationLabel} ({roomLabel} · {guestLabel})',
            description: 'Tour breakdown duration with room and guest counts',
          },
          {
            durationLabel,
            roomLabel: intl.formatMessage(
              {
                id: 'zPUYJOin2Pl7y-_0pIiI',
                defaultMessage: '{count, plural, one {# room} other {# rooms}}',
                description: 'Room count in tour breakdown info on the tour offer detail page',
              },
              { count: itemViews.length },
            ),
            guestLabel: intl.formatMessage(
              {
                id: '3b_Ypox6e3CMBJmT3obQ',
                defaultMessage: '{count, plural, one {# guest} other {# guests}}',
                description: 'Guest count in tour breakdown info on the tour offer detail page',
              },
              { count: guestCount },
            ),
          },
        ),
    ],
    items: isTourPostPurchase && isOriginalOrderItem ? [] : items,
    itemsAlwaysVisible: items.length === 1,
    alwaysShowItemPrice: !hidePrice,
    price: sum(items, item => item.price || 0),
    memberPrice: sum(items, item => item.memberPrice || 0),
    variation: selectedVariation,
    ...(commissionData && offer?.id && {
      commission: {
        commissionPercentage,
        itemDiscount: totalCommissionDiscount,
      },
    }),
  }
}

export const getTourExperienceV2BreakdownView = createSelector(
  (state: App.State) => getTourV2ExperienceItemsViews(state),
  (state: App.State) => state.checkout.commissionData,
  (viewWithStatus, commissionData): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) { return { hasRequiredData: viewWithStatus.hasRequiredData, data: [] } }

    const intl = getCurrentIntl()
    const items = viewWithStatus.data.map<App.Checkout.TourV2ExperienceItemBreakdownView>((item: App.Checkout.TourV2ExperienceItemView) => {
      const { purchasableOption, occupants } = item.item
      return {
        title: item.primaryTitle,
        additionalInfo: [
          {
            text: intl.formatMessage(
              { id: 'zR-Mn63KzWljTaxXLJ7q', defaultMessage: 'Day {day} · {date} · {timeSlot} ({guests})', description: 'Tour experience breakdown line showing day number, date, time slot and guest count' },
              {
                day: purchasableOption.day,
                date: moment(purchasableOption.date).format(DMY_CASUAL_FORMAT),
                timeSlot: purchasableOption.timeSlot ?? '',
                guests: intl.formatMessage(
                  {
                    id: 'kMtX0sUoAMX-ssUhRQB9',
                    defaultMessage: '{count, plural, one {# guest} other {# guests}}',
                    description: 'Guest count in tour optional experience breakdown in the checkout tour selection',
                  },
                  { count: sumUpOccupancies(occupants) },
                ),
              },
            ),
          },
        ],
        additionalElements: [],
        transactionKey: item.item.transactionKey,
        price: item.item.total,
        offerId: item.item.offerId,
        itemId: item.item.itemId,
        itemType: 'tourV2Experience',
        taxesAndFees: item.totals.taxesAndFees,
      }
    })

    const totalCommissionDiscount = !commissionData ? 0 : sum(items, item => commissionData.itemsCommission[getAgentHubCommissionItemKey(item)]?.itemDiscount ?? 0)
    const commissionPercentage = Number((totalCommissionDiscount / sum(items, item => item.price || 0) * 100).toFixed(2))

    return {
      hasRequiredData: viewWithStatus.hasRequiredData,
      data: [{
        title: intl.formatMessage({
          id: 'HpFniwL28qniAWXvjXFc',
          defaultMessage: 'Optional experiences',
          description: 'Title for tour optional experiences section in checkout breakdown',
        }),
        offerType: 'tour_experience',
        description: '',
        additionalInfoText: [
          intl.formatMessage({
            id: 'sCvr858k3x5uf8ZorxAP',
            defaultMessage: '{count, plural, one {# experience} other {# experiences}}',
            description: 'Experience count in tour optional experiences breakdown',
          }, { count: items.length }),
        ],
        items,
        itemsAlwaysVisible: false,
        price: sum(items, item => item.price || 0),
        memberPrice: sum(items, item => item.memberPrice || 0),
        ...(commissionData && {
          commission: {
            commissionPercentage,
            itemDiscount: totalCommissionDiscount,
          },
        }),
      }],
    }
  })
